import React, { useRef } from "react";
import featureImg from "../../assets/l1.jpeg";
import { motion, useInView } from "framer-motion";

/* ================= ANIMATION VARIANTS ================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const popUpVariants = {
  hidden: {
    opacity: 0,
    scale: 0.85,
    y: 30,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const imageVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* ================= COMPONENT ================= */

export default function CoreFeatures() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section ref={ref} className="w-full bg-[#092CB4] py-24">
      <div className="max-w-[1300px] mx-auto px-6 sm:px-10">

        {/* ===== Heading ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] leading-tight font-semibold text-white">
            Core Features That Set Us Apart in Study Abroad Guidance
          </h2>
          <p className="mt-4 text-[14px] sm:text-[15px] text-white/70">
            Discover our key strengths designed to deliver trusted, transparent, and student-focused overseas education support.
          </p>
        </motion.div>

        {/* ===== Content Grid ===== */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-center"
        >

          {/* ===== Left Cards ===== */}
          <div className="flex flex-col gap-6 order-2 lg:order-1">
            <FeatureCard
              icon="📊"
              title="Door-to-Door Counselling"
              desc="Personalized counselling at your home for students and parents."
            />
            <FeatureCard
              icon="🧾"
              title="Expert University Selection"
              desc="Right course and university recommendations based on profile and goals."
            />
          </div>

          {/* ===== Center Image ===== */}
          <motion.div
            variants={imageVariants}
            className="flex justify-center order-1 lg:order-2"
          >
            <div className="rounded-3xl overflow-hidden w-[280px] sm:w-[340px] lg:w-[360px] h-[360px] sm:h-[420px] lg:h-[440px] bg-white shadow-2xl">
              <img
                src={featureImg}
                alt="Feature"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* ===== Right Cards ===== */}
          <div className="flex flex-col gap-6 order-3">
            <FeatureCard
              icon="📱"
              title="End-to-End Application Support"
              desc="Complete assistance from applications to admission offers."
            />
            <FeatureCard
              icon="🔐"
              title="Visa Guidance & Interview Prep"
              desc="Step-by-step visa processing with expert interview preparation."
            />
          </div>

        </motion.div>
      </div>
    </section>
  );
}

/* ================= FEATURE CARD ================= */

function FeatureCard({ icon, title, desc }) {
  return (
    <motion.div
      variants={popUpVariants}
      className="bg-white rounded-2xl p-6 shadow-lg"
    >
      <div className="w-10 h-10 bg-[#092CB4]/10 rounded-md flex items-center justify-center mb-4 text-lg">
        {icon}
      </div>
      <h4 className="text-[#092CB4] font-medium mb-1">
        {title}
      </h4>
      <p className="text-black text-sm leading-6">
        {desc}
      </p>
    </motion.div>
  );
}
